import { styled } from '@linaria/react';
import LegendToggleIcon from '@mui/icons-material/LegendToggle';
import { IconButton } from '@mui/material';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { formatKilometers } from '../../../core/mapV2/format';
import { activeDisplayTitleQuery, legendModeQuery, referenceColoringQuery } from '../../../core/mapV2/queries';
import { legendModeCycled } from '../../../core/mapV2/state';
import theme from '../../../design/theme';
import { useAppDispatch, useAppSelector } from '../../../types';

const Card = styled.div`
    position: absolute;
    z-index: 1;
    left: 12px;
    bottom: 12px;
    min-width: 220px;
    max-width: 300px;
    box-sizing: border-box;
    padding: 8px 8px 10px 12px;
    border-radius: 8px;
    background-color: ${theme.color.white.main};
    box-shadow: ${theme.boxShadow.light};
`;

const Head = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const Title = styled.span`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    color: ${theme.color.grey.dark};
`;

const Entries = styled.ul`
    margin: 8px 0 0;
    padding: 0;
    max-height: 260px;
    overflow-y: auto;
    list-style: none;
    font-size: 12px;
    color: ${theme.color.grey.dark};
`;

const Entry = styled.li`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 2px 0;
`;

const Swatch = styled.span`
    flex: 0 0 auto;
    width: 18px;
    height: 4px;
    border-radius: 2px;
`;

const EntryLabel = styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const EntryLength = styled.span`
    flex: 0 0 auto;
    color: ${theme.color.grey.light};
`;

const MiniSwatches = styled.div`
    display: flex;
    gap: 3px;
    margin-top: 6px;
`;

const Toggle = styled.div`
    position: absolute;
    z-index: 1;
    left: 12px;
    bottom: 12px;
    border-radius: 6px;
    background-color: ${theme.color.white.main};
    box-shadow: ${theme.boxShadow.light};
`;

// Floating legend, bottom left of the map. The title follows the displayed layer and its indicator
// ("RÉFÉRENTIEL > CATÉGORIE"). One button cycles full -> mini -> hidden (a lone toggle). Full lists the
// indicator's values with their colors and length; mini keeps only the color chips.
export const Legend: FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const mode = useAppSelector(legendModeQuery);
    const title = useAppSelector(activeDisplayTitleQuery);
    const coloring = useAppSelector(referenceColoringQuery);
    const entries = coloring?.entries ?? [];
    const cycleLabel = t({ full: 'mapV2.legend.reduce', mini: 'mapV2.legend.hide', hidden: 'mapV2.legend.show' }[mode]);
    const cycle = (
        <IconButton
            size="small"
            aria-label={cycleLabel}
            title={cycleLabel}
            onClick={() => dispatch(legendModeCycled())}
        >
            <LegendToggleIcon fontSize="small" />
        </IconButton>
    );

    if (mode === 'hidden') {
        return <Toggle data-testid="map-v2-legend-toggle">{cycle}</Toggle>;
    }

    return (
        <Card data-testid="map-v2-legend" data-mode={mode}>
            <Head>
                {mode === 'full' ? <Title>{title ?? t('mapV2.legend.noLayer')}</Title> : null}
                {cycle}
            </Head>
            {mode === 'full' && entries.length > 0 ? (
                <Entries data-testid="map-v2-legend-entries">
                    {entries.map((entry) => (
                        <Entry key={`${entry.kind}-${entry.label}`}>
                            <Swatch style={{ backgroundColor: entry.color }} />
                            <EntryLabel>
                                {entry.kind === 'value' ? entry.label : t(`mapV2.legend.${entry.kind}`)}
                            </EntryLabel>
                            <EntryLength>{formatKilometers(entry.lengthMeters)}</EntryLength>
                        </Entry>
                    ))}
                </Entries>
            ) : null}
            {mode === 'mini' && entries.length > 0 ? (
                <MiniSwatches>
                    {entries.map((entry) => (
                        <Swatch key={`${entry.kind}-${entry.label}`} style={{ backgroundColor: entry.color }} />
                    ))}
                </MiniSwatches>
            ) : null}
        </Card>
    );
};
